// src/admin/pages/AdminCharts.jsx

import React, { useMemo } from 'react';

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from 'recharts';


/* =========================================================
   HELPERS
========================================================= */

function numberValue(value) {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return 0;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : 0;
}


function formatCurrency(value) {
  const amount = numberValue(value);

  if (amount >= 10000000) {
    return `₹${(amount / 10000000).toFixed(1)}Cr`;
  }

  if (amount >= 100000) {
    return `₹${(amount / 100000).toFixed(1)}L`;
  }

  if (amount >= 1000) {
    return `₹${(amount / 1000).toFixed(1)}K`;
  }

  return `₹${Math.round(amount)}`;
}


function formatFullCurrency(value) {
  return `₹${numberValue(value).toLocaleString('en-IN', {
    maximumFractionDigits: 0,
  })}`;
}


/* =========================================================
   CHART CARD
========================================================= */

function ChartCard({
  title,
  subtitle,
  children,
  className = '',
}) {
  return (
    <div
      className={`
        rounded-2xl
        border border-gray-100
        bg-white
        p-5
        shadow-sm
        ${className}
      `}
    >
      <div className="mb-4">
        <h3 className="text-base font-semibold text-[#243B3B]">
          {title}
        </h3>

        {subtitle && (
          <p className="mt-1 text-xs text-gray-500">
            {subtitle}
          </p>
        )}
      </div>

      {children}
    </div>
  );
}


/* =========================================================
   EMPTY
========================================================= */

function EmptyChart() {
  return (
    <div className="flex h-[280px] items-center justify-center rounded-xl bg-gray-50">
      <p className="text-sm text-gray-400">
        No data available
      </p>
    </div>
  );
}


/* =========================================================
   FINANCIAL TOOLTIP
========================================================= */

function FinancialTooltip({
  active,
  payload,
}) {
  if (!active || !payload || !payload.length) {
    return null;
  }

  return (
    <div className="rounded-xl border border-gray-100 bg-white px-4 py-3 shadow-lg">

      <p className="mb-2 text-sm font-semibold text-[#243B3B]">
        Financial Overview
      </p>

      {payload.map((item) => (
        <div
          key={item.dataKey}
          className="flex items-center justify-between gap-6 text-sm"
        >
          <span className="text-gray-500">
            {item.name}
          </span>

          <span className="font-semibold text-gray-800">
            {formatFullCurrency(item.value)}
          </span>
        </div>
      ))}

    </div>
  );
}


/* =========================================================
   CLIENT MARGIN TOOLTIP
========================================================= */

function MarginTooltip({
  active,
  payload,
}) {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const client = payload[0]?.payload;

  return (
    <div className="rounded-xl border border-gray-100 bg-white px-4 py-3 shadow-lg">

      <p className="mb-2 text-sm font-semibold text-[#243B3B]">
        {client?.name}
      </p>

      <div className="space-y-1 text-sm">

        <p className="text-gray-500">
          Billing:{' '}
          <span className="font-semibold text-gray-800">
            {formatFullCurrency(client?.billing)}
          </span>
        </p>

        <p className="text-gray-500">
          Company Cost:{' '}
          <span className="font-semibold text-gray-800">
            {formatFullCurrency(client?.cost)}
          </span>
        </p>

        <p className="text-gray-500">
          Margin:{' '}
          <span className="font-semibold text-[#437B78]">
            {client?.margin?.toFixed(2)}%
          </span>
        </p>

        <p className="text-gray-500">
          Patients:{' '}
          <span className="font-semibold text-gray-800">
            {client?.patients?.toLocaleString('en-IN')}
          </span>
        </p>

        <p className="text-gray-500">
          Camps:{' '}
          <span className="font-semibold text-gray-800">
            {client?.camps}
          </span>
        </p>

      </div>

    </div>
  );
}


/* =========================================================
   ADMIN CHARTS
========================================================= */

export default function AdminCharts({
  summary = {},
  clients = [],
}) {


  /* =======================================================
     1. OVERALL BILLING VS COST

     Using:

     summary.total_billing
     summary.total_company_cost
  ======================================================= */

  const financialData = useMemo(() => {

    return [
      {
        name: 'Overall',

        Billing: numberValue(
          summary?.total_billing
        ),

        Cost: numberValue(
          summary?.total_company_cost
        ),
      },
    ];

  }, [summary]);


  /* =======================================================
     2. OVERALL MARGIN

     Using:

     summary.margin_percentage
  ======================================================= */

  const marginPercentage = numberValue(
    summary?.margin_percentage
  );


  const marginData = useMemo(() => {

    const margin = Math.min(
      Math.max(marginPercentage, 0),
      100
    );

    return [
      {
        name: 'Margin',
        value: margin,
      },
      {
        name: 'Remaining',
        value: Math.max(
          100 - margin,
          0
        ),
      },
    ];

  }, [marginPercentage]);


  /* =======================================================
     3. CLIENT DATA

     Financial:

     client.total_billing
     client.total_company_cost
     client.margin_percentage

     Patients:

     client.locations[].camps[].patient_count
  ======================================================= */

  const clientData = useMemo(() => {

    return clients.map((client) => {

      let patients = 0;

      let campCount = 0;

      let completedCamps = 0;

      let scheduledCamps = 0;


      const locations = Array.isArray(
        client?.locations
      )
        ? client.locations
        : [];


      locations.forEach((location) => {

        const camps = Array.isArray(
          location?.camps
        )
          ? location.camps
          : [];


        campCount += camps.length;


        camps.forEach((camp) => {

          patients += numberValue(
            camp?.patient_count
          );


          if (
            camp?.status === 'COMPLETED'
          ) {
            completedCamps += 1;
          }


          if (
            camp?.status === 'SCHEDULED'
          ) {
            scheduledCamps += 1;
          }

        });

      });


      return {

        name:
          client?.client_name ||
          `Client ${client?.client_id}`,

        clientId:
          client?.client_id,

        billing:
          numberValue(
            client?.total_billing
          ),

        cost:
          numberValue(
            client?.total_company_cost
          ),

        margin:
          numberValue(
            client?.margin_percentage
          ),

        patients,

        camps:
          numberValue(
            client?.camp_count
          ) || campCount,

        completedCamps,

        scheduledCamps,

      };

    });

  }, [clients]);


  /* =======================================================
     4. CLIENT MARGIN
  ======================================================= */

  const clientMarginData = useMemo(() => {

    return clientData
      .filter(
        (client) =>
          client.billing > 0 ||
          client.margin > 0
      )
      .sort(
        (a, b) =>
          b.margin - a.margin
      );

  }, [clientData]);


  /* =======================================================
     5. CLIENT PATIENTS
  ======================================================= */

  const clientPatientData = useMemo(() => {

    return clientData
      .filter(
        (client) =>
          client.patients > 0
      )
      .sort(
        (a, b) =>
          b.patients - a.patients
      );

  }, [clientData]);


  /* =======================================================
     6. CLIENT CAMPS
  ======================================================= */

  const clientCampData = useMemo(() => {

    return clientData
      .filter(
        (client) =>
          client.camps > 0
      )
      .sort(
        (a, b) =>
          b.camps - a.camps
      );

  }, [clientData]);


  return (

    <div className="space-y-5">


      {/* =================================================
          HEADER
      ================================================= */}

      <div>

        <h2 className="text-sm font-semibold uppercase tracking-wide text-[#6B7C7C]">
          Business Overview
        </h2>

        <p className="mt-1 text-sm text-gray-500">
          Billing, cost, margin and operational performance
        </p>

      </div>


      {/* =================================================
          BILLING / COST / MARGIN
      ================================================= */}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">


        {/* ===============================================
            BILLING VS COST
        =============================================== */}

        <ChartCard
          title="Billing vs Company Cost"
          subtitle="Overall financial performance"
          className="lg:col-span-2"
        >

          {financialData[0]?.Billing ||
          financialData[0]?.Cost ? (

            <div className="h-[300px]">

              <ResponsiveContainer
                width="100%"
                height="100%"
              >

                <BarChart
                  data={financialData}
                  margin={{
                    top: 10,
                    right: 10,
                    left: 10,
                    bottom: 10,
                  }}
                  barGap={15}
                >

                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                  />


                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                  />


                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={
                      formatCurrency
                    }
                  />


                  <Tooltip
                    content={
                      <FinancialTooltip />
                    }
                  />


                  <Legend />


                  <Bar
                    dataKey="Billing"
                    name="Billing"
                    fill="#437B78"
                    radius={[
                      8,
                      8,
                      0,
                      0,
                    ]}
                    barSize={65}
                  />


                  <Bar
                    dataKey="Cost"
                    name="Company Cost"
                    fill="#D9A441"
                    radius={[
                      8,
                      8,
                      0,
                      0,
                    ]}
                    barSize={65}
                  />

                </BarChart>

              </ResponsiveContainer>

            </div>

          ) : (

            <EmptyChart />

          )}

        </ChartCard>


        {/* ===============================================
            MARGIN
        =============================================== */}

        <ChartCard
          title="Overall Margin"
          subtitle="Margin percentage for selected date"
        >

          <div className="relative h-[300px]">

            <ResponsiveContainer
              width="100%"
              height="100%"
            >

              <PieChart>

                <Pie
                  data={marginData}
                  cx="50%"
                  cy="50%"
                  innerRadius={78}
                  outerRadius={108}
                  paddingAngle={3}
                  dataKey="value"
                  startAngle={90}
                  endAngle={-270}
                >

                  <Cell
                    fill="#437B78"
                  />

                  <Cell
                    fill="#E8EEEE"
                  />

                </Pie>

              </PieChart>

            </ResponsiveContainer>


            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">

              <span className="text-3xl font-bold text-[#243B3B]">
                {marginPercentage.toFixed(1)}%
              </span>

              <span className="mt-1 text-xs text-gray-500">
                Margin
              </span>

            </div>

          </div>

        </ChartCard>

      </div>


      {/* =================================================
          CLIENT MARGIN
      ================================================= */}

      <ChartCard
        title="Margin by Client"
        subtitle="Client-wise margin percentage"
      >

        {clientMarginData.length > 0 ? (

          <div className="h-[340px]">

            <ResponsiveContainer
              width="100%"
              height="100%"
            >

              <BarChart
                data={clientMarginData}
                layout="vertical"
                margin={{
                  top: 10,
                  right: 30,
                  left: 30,
                  bottom: 10,
                }}
              >

                <CartesianGrid
                  strokeDasharray="3 3"
                  horizontal={false}
                />


                <XAxis
                  type="number"
                  domain={[
                    0,
                    100,
                  ]}
                  tickFormatter={(value) =>
                    `${value}%`
                  }
                  axisLine={false}
                  tickLine={false}
                />


                <YAxis
                  type="category"
                  dataKey="name"
                  width={150}
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fontSize: 11,
                  }}
                />


                <Tooltip
                  content={
                    <MarginTooltip />
                  }
                />


                <Bar
                  dataKey="margin"
                  name="Margin"
                  fill="#437B78"
                  radius={[
                    0,
                    8,
                    8,
                    0,
                  ]}
                  label={{
                    position: 'right',
                    formatter: (value) =>
                      `${Number(value).toFixed(1)}%`,
                  }}
                />

              </BarChart>

            </ResponsiveContainer>

          </div>

        ) : (

          <EmptyChart />

        )}

      </ChartCard>


      {/* =================================================
          OPERATIONAL OVERVIEW
      ================================================= */}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">


        {/* ===============================================
            PATIENTS
        =============================================== */}

        <ChartCard
          title="Patients by Client"
          subtitle="Total patients across camps"
        >

          {clientPatientData.length > 0 ? (

            <div className="h-[320px]">

              <ResponsiveContainer
                width="100%"
                height="100%"
              >

                <BarChart
                  data={clientPatientData}
                  layout="vertical"
                  margin={{
                    top: 10,
                    right: 20,
                    left: 20,
                    bottom: 10,
                  }}
                >

                  <CartesianGrid
                    strokeDasharray="3 3"
                    horizontal={false}
                  />


                  <XAxis
                    type="number"
                    axisLine={false}
                    tickLine={false}
                  />


                  <YAxis
                    type="category"
                    dataKey="name"
                    width={150}
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fontSize: 11,
                    }}
                  />


                  <Tooltip
                    formatter={(value) =>
                      `${Number(value).toLocaleString(
                        'en-IN'
                      )} patients`
                    }
                  />


                  <Bar
                    dataKey="patients"
                    name="Patients"
                    fill="#6E9E97"
                    radius={[
                      0,
                      7,
                      7,
                      0,
                    ]}
                  />

                </BarChart>

              </ResponsiveContainer>

            </div>

          ) : (

            <EmptyChart />

          )}

        </ChartCard>


        {/* ===============================================
            CAMPS
        =============================================== */}

        <ChartCard
          title="Camps by Client"
          subtitle="Total camps for selected date"
        >

          {clientCampData.length > 0 ? (

            <div className="h-[320px]">

              <ResponsiveContainer
                width="100%"
                height="100%"
              >

                <BarChart
                  data={clientCampData}
                  margin={{
                    top: 10,
                    right: 20,
                    left: 10,
                    bottom: 10,
                  }}
                >

                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                  />


                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fontSize: 11,
                    }}
                  />


                  <YAxis
                    allowDecimals={false}
                    axisLine={false}
                    tickLine={false}
                  />


                  <Tooltip
                    formatter={(value) =>
                      `${Number(value)} camps`
                    }
                  />


                  <Bar
                    dataKey="camps"
                    name="Camps"
                    fill="#437B78"
                    radius={[
                      7,
                      7,
                      0,
                      0,
                    ]}
                  />

                </BarChart>

              </ResponsiveContainer>

            </div>

          ) : (

            <EmptyChart />

          )}

        </ChartCard>

      </div>


      {/* =================================================
          CAMP STATUS
      ================================================= */}

      <ChartCard
        title="Camp Status"
        subtitle="Completed and scheduled camps"
      >

        {clientData.length > 0 ? (

          <div className="h-[300px]">

            <ResponsiveContainer
              width="100%"
              height="100%"
            >

              <BarChart
                data={clientData}
                margin={{
                  top: 10,
                  right: 20,
                  left: 10,
                  bottom: 10,
                }}
              >

                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                />


                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{
                    fontSize: 11,
                  }}
                />


                <YAxis
                  allowDecimals={false}
                  axisLine={false}
                  tickLine={false}
                />


                <Tooltip />


                <Legend />


                <Bar
                  dataKey="completedCamps"
                  name="Completed"
                  fill="#437B78"
                  radius={[
                    6,
                    6,
                    0,
                    0,
                  ]}
                />


                <Bar
                  dataKey="scheduledCamps"
                  name="Scheduled"
                  fill="#D9A441"
                  radius={[
                    6,
                    6,
                    0,
                    0,
                  ]}
                />

              </BarChart>

            </ResponsiveContainer>

          </div>

        ) : (

          <EmptyChart />

        )}

      </ChartCard>

    </div>
  );
}